import { getModel } from '../../model/registry'
import { useCallback, useEffect, useMemo, useState, useRef } from "react"
import { useSnapshot } from "valtio"
import ViewTitle from "./ViewTitle"
import CreateButton from "./CreateButton"
import ListDropZone from './ListDropZone'
import ViewFilter from "./ViewFilter"
import useGroupFilter from "../filter/useGroupFilter"
import { headerStore } from "../../state/headerStore"
import ConnectFilter from "./ConnectFilter"
import { useLocation, useParams, useSearchParams } from "react-router-dom"
import { findMenuItem } from "../header/HeaderTitle"
import { documentStore } from "../../state/documentStore"

import { DeadSpace } from "../mobileNav/MobileNavBar" 
import useActiveElement from "../../hooks/useActiveElement"
import { isMonth, thisMonth } from "./MonthFilter"
import { getFromLS, saveToLS } from "../../utils/localStore/localStore"
import generateEndpoint from "../../utils/generateEndpoint"
import { ModalProvider } from "../modal/useModalDialog"
import { useMessage } from "../pageView/Page"
import MessageBar from "../toast/MessageBar"

// Data + timing hooks
import useDebounce from "../../hooks/useDebounce"
import useStaleTime from "../../hooks/useStaleTime"
import useListData from "../../hooks/useListData"
import useApi from "../../hooks/useApi"
import useScreenSize from "../../hooks/useScreenSize"

// Presentational table
import ViewTable from "./ViewTable"

const ListView = ({ type, options, selectAction, onSelectConfirm, embedded = false }) => {
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const docSnap = useSnapshot(documentStore)

  if (!getModel('listViewData')[type]) return <h2>Nothing to display, please define the list view for : "{type}"</h2>
  const config = getModel('listViewData')[type]

  // Resolve the initial filter tab: ?tab=slug overrides localStorage
  let currentActiveFilter = getFromLS(`activeList`, type) ?? 0
  const tabParam = searchParams.get('tab')
  if (tabParam && config.filters?.list) {
    const tabIndex = config.filters.list.findIndex(f => f.slug === tabParam)
    if (tabIndex >= 0) currentActiveFilter = tabIndex
  }

  // This manages which Filter tab is selected and saves it to LocalStorage, so it remembers.
  const { activeElem, handleActiveElem } = useActiveElement(`activeList`, currentActiveFilter, type)

  if (!options) {
    options = {
      viewFilters: true,
    }
  }

  // On a wide screen a page-level list's title — and its back button — are in
  // the header, and its New button is at the top right of ListTopBar. A list
  // embedded in a tab, step or widget keeps its own title row, and so does one
  // the menu does not lead to directly (the header would show another title).
  const { isWide } = useScreenSize()
  const { pathname } = useLocation()
  const titleInHeader = isWide && !embedded
    && findMenuItem(pathname)?.path === pathname.replace(/\/+$/, '')
    && config?.layout?.showTitle !== false
  const backInHeader = titleInHeader && !!config?.layout?.showBackButton
  useEffect(() => {
    if (!backInHeader) return
    headerStore.back = true
    return () => { headerStore.back = false }
  }, [backInHeader])

  const showFilterBar = config?.layout?.showFilterBar !== false && options.viewFilters

  const { getMe } = useApi()
  // Row actions report blocked deletes/operations here (see useLineActions)
  const [message, setMessage] = useMessage((s) => s)
  const [aggregateCounts, setAggregateCounts] = useState({})
  const aggregateFetched = useRef(false)

  const fetchAggregateCounts = useCallback(() => {
    const aggregateEndpoint = config?.filters?.aggregate
    if (!aggregateEndpoint) return
    getMe(aggregateEndpoint).then(res => {
      if (!res?.data) return
      const counts = {}
      config.filters.list.forEach(f => {
        if (!f.aggregateMatch) return
        const row = res.data.find(r =>
          Object.entries(f.aggregateMatch).every(([k, v]) => String(r[k]) === String(v))
        )
        if (row) counts[f.label] = Number(row.count)
      })
      setAggregateCounts(counts)
    }).catch(() => {})
  }, [config])

  useEffect(() => {
    if (aggregateFetched.current) return
    aggregateFetched.current = true
    fetchAggregateCounts()
  }, [])

  // Controlled state: search and location filter (user-applied)
  const [searchTerm, setSearchTerm] = useState('')

  // User-applied location filter state (only used for tables with location filter UI)
  const [userFilterLabel, setUserFilterLabel] = useState('')
  const [userFilterTerm, setUserFilterTerm] = useState(false)
  const [groupFilterTerm, setGroupFilterTerm] = useState('')
  const [connectFilterTerm, setConnectFilterTerm] = useState(false)

  // The calendar month a transaction list is narrowed to. It starts on the
  // current month and is remembered per list, so coming back to the journals
  // lands where the user left rather than jumping to today.
  const [monthValue, setMonthValue] = useState(() => getFromLS('monthFilter', type) ?? thisMonth())

  const onMonthFilterChange = (next) => {
    setMonthValue(next)
    saveToLS('monthFilter', type, next)
  }

  const onApplyLocationFilter = (locationData) => {
    const label = locationData?.id || ''
    setUserFilterLabel(label)
    setUserFilterTerm(label ? `id:${label}` : false)
  }

  const onClearFilter = () => {
    setUserFilterLabel('')
    setUserFilterTerm(false)
  }

  // Active filter definition
  const currentFilter = config?.filters?.list?.[activeElem]

  // Effective entity and table derived from current filter (no intermediate state).
  // A tab may name its own `table` when it lists the same entity with other
  // columns — a booked expense has a payment status, a draft one has none.
  const effectiveEntity = currentFilter?.entity || config.entity
  const tableKey = currentFilter?.table ?? effectiveEntity
  const table = config.tables[tableKey]

  // The column sort, remembered per list and per table — tabs that show other
  // columns keep a sort of their own, so switching tabs never carries a sort
  // onto a table that has no such column.
  const [orders, setOrders] = useState(() => getFromLS('listOrder', type) ?? {})
  const order = orders[tableKey]

  const setOrder = useCallback((next) => {
    setOrders(prev => ({
      ...prev,
      [tableKey]: typeof next === 'function'
        ? next(prev[tableKey] ?? [table?.orderBy, table?.orderDirection || 'asc'])
        : next,
    }))
  }, [tableKey, table])

  useEffect(() => {
    saveToLS('listOrder', type, orders)
  }, [orders, type])

  // Endpoint to call: the filter's own entity, else the config's.
  // `endpoint` defaults to `entity` — declare it only for a subcollection
  // template like 'product/:id/bomVersion', which generateEndpoint fills in.
  const endpoint = currentFilter?.entity
    ? currentFilter.entity // filter-level entity maps to endpoint directly
    : generateEndpoint(config.endpoint ?? config.entity, id)

  // Whether this list shows the month filter, and on which date. The active tab
  // decides — a journal's drafts are a working set and want no month, its
  // posted and reversed entries are the book and do — and a list with no tabs
  // says so on the table instead. The date field is the table's own, because it
  // belongs to the entity rather than to the tab.
  const monthFilterField = table?.monthFilterField
  const showMonthFilter = Boolean(monthFilterField)
    && (currentFilter?.applyMonthFilter ?? table?.applyMonthFilter ?? false)

  const onConnectFilterChange = (filter) => {
    setConnectFilterTerm(filter ? `${filter.field}:${filter.value}` : false)
  }

  const groupFilter = useGroupFilter({
    listKey: type,
    config: showFilterBar ? config.groupFilters : undefined,
    onChange: setGroupFilterTerm,
  })

  // What ListTopBar shows above the rows; the drop zone stays above it all.
  const topBar = {
    cards: showFilterBar && (config.filters?.list ?? []).some(f => f?.showFilter !== false)
      ? <ViewFilter filters={config.filters} filter={activeElem} setFilter={handleActiveElem} counts={aggregateCounts} />
      : null,
    extras: (groupFilter.selectors || config.connectFilter)
      ? <>
          {groupFilter.selectors}
          {config.connectFilter && (
            <ConnectFilter config={config.connectFilter} listType={type} onFilterChange={onConnectFilterChange} />
          )}
        </>
      : null,
    tags: groupFilter.tags,
    createButton: titleInHeader
      ? <CreateButton listHeader={config.listHeader} entity={config.entity} />
      : null,
  }

  // Clear the user location filter when switching top-level filter
  useEffect(() => {
    setUserFilterLabel('')
    setUserFilterTerm(false)
    setGroupFilterTerm('')
  }, [effectiveEntity])

  // Effective order: the remembered sort while its column is still on the
  // table (config changes can drop one), otherwise the table defaults
  const effectiveOrder = useMemo(() => {
    const known = Array.isArray(order) && order[0]
      && (order[0] === table?.orderBy || table?.columns?.some(c => c.id === order[0]))
    if (known) return order
    return [table?.orderBy, table?.orderDirection || 'asc']
  }, [order, table])

  // Compute static filterTerm and serverFilter strings from config + active filter
  const { filterTerm, serverFilter } = useMemo(() => {
    const toPair = (f) => `${f.id}:${f.value === '{id}' ? id : f.value}`
    const client = currentFilter?.applyFilter && currentFilter?.terms?.length
      ? currentFilter.terms.map(toPair).join(';')
      : false
    const configServer = currentFilter?.applyFilter && currentFilter?.serverTerms?.length
      ? currentFilter.serverTerms.map(toPair).join(';')
      : false
    const fixedServer = options?.fixedServerFilter || false
    const folderFilter = config.folderFilter && docSnap.selectedFolderId
      ? `folderId:${docSnap.selectedFolderId}`
      : false
    const serverParts = [configServer, fixedServer, connectFilterTerm, folderFilter].filter(Boolean)
    const server = serverParts.length ? serverParts.join(';') : false
    return { filterTerm: client, serverFilter: server }
  }, [currentFilter, id, options?.fixedServerFilter, connectFilterTerm, config.folderFilter, docSnap.selectedFolderId])

  // The selected month as a date range the query filters on. Plain YYYY-MM-DD
  // bounds: the server widens them to the whole first and last day, and dates
  // are stored at midday, so a month holds exactly the days it should.
  const monthFilterTerm = useMemo(() => {
    if (!showMonthFilter || !isMonth(monthValue)) return false
    const { year, month } = monthValue
    const mm = String(month + 1).padStart(2, '0')
    const lastDay = new Date(year, month + 1, 0).getDate()
    return `dateField:${monthFilterField};from:${year}-${mm}-01;to:${year}-${mm}-${lastDay}`
  }, [showMonthFilter, monthValue, monthFilterField])

  // Combine static filterTerm with user-applied location filter
  const combinedFilterTerm = useMemo(() => {
    const parts = []
    if (filterTerm) parts.push(filterTerm)
    if (userFilterTerm) parts.push(userFilterTerm)
    if (groupFilterTerm) parts.push(groupFilterTerm)
    if (monthFilterTerm) parts.push(monthFilterTerm)
    return parts.length ? parts.join(';') : false
  }, [filterTerm, userFilterTerm, groupFilterTerm, monthFilterTerm])

  // Debounced search
  const debouncedSearchTerm = useDebounce(searchTerm, 1000)

  // Stale time can depend on effective entity (filter-level or global)
  const staleTime = useStaleTime(effectiveEntity)

  // Centralized data fetching - single source of truth
  const result = useListData({
    endpoint,
    order: effectiveOrder,
    searchTerm: debouncedSearchTerm,
    filterTerm: combinedFilterTerm,
    serverFilter,
    // A baseline filter applying to every tab of this list, written in the shared
    // predicate language rather than the `key:value` filter dialect.
    conditions: config.defaultFilters,
    staleTime,
    queryParams: config.queryParams,
  })

  return (
    <ModalProvider>
      {message.show && (
        <MessageBar title={message.title} message={message.message} type={message.type} setMessage={setMessage} />
      )}
      {config?.layout?.showTitle !== false && !titleInHeader && <ViewTitle config={config} />}
      {config.dropZone && endpoint && <ListDropZone config={config.dropZone} listEndpoint={endpoint} testId={`${config.entity}.list.dropZone`} />}
      {endpoint && (
        <ViewTable
          table={table}
          config={config}
          endpoint={endpoint}
          filter={activeElem}
          id={id}
          // Controlled + data
          result={result}
          order={effectiveOrder}
          setOrder={setOrder}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          debouncedSearchTerm={debouncedSearchTerm}
          filterTerm={combinedFilterTerm}
          serverFilter={serverFilter}
          entity={effectiveEntity}
          userFilterLabel={userFilterLabel}
          onApplyLocationFilter={onApplyLocationFilter}
          onClearFilter={onClearFilter}
          selectAction={selectAction ?? config.selectAction}
          onSelectConfirm={onSelectConfirm}
          onRefreshAggregate={fetchAggregateCounts}
          monthFilter={showMonthFilter ? { value: monthValue } : undefined}
          onMonthFilterChange={onMonthFilterChange}
          topBar={topBar}
        />
      )}
      {config?.layout?.showNavSpacer !== false && <DeadSpace />}
    </ModalProvider>
  )
}

export default ListView
